import moment from "moment-timezone";
import { settledEarlyStatus } from "../../utility/entryCopy";

/** Align with betting UI / backend live window (see gameController.isGameLive). */
const MATCH_TZ = "America/Chicago";
const LIVE_WINDOW_MINUTES = 180;

export function getGamePhase(gameTime) {
  if (!gameTime) return "unknown";
  const now = moment();
  const matchMoment = moment.tz(gameTime, MATCH_TZ);
  const diffMin = matchMoment.diff(now, "minutes");
  if (diffMin > 0) return "upcoming";
  if (diffMin > -LIVE_WINDOW_MINUTES) return "live";
  return "ended";
}

function normalizeLegStatus(winStatus) {
  const s = String(winStatus ?? "").trim().toUpperCase();
  if (s === "WIN") return "WIN";
  if (s === "LOSS") return "LOSS";
  return "PENDING";
}

/**
 * User-facing lifecycle for open/settled tickets (Active Bets, wager modal).
 * @returns {{ label: string, variant: string, detail: string, legsSummary?: string }}
 */
export function getBetLifecycleStatus(item) {
  if (!item) {
    return { label: "Unknown", variant: "pending", detail: "" };
  }

  // Checked on the raw status: normalizeLegStatus() folds everything that is
  // not WIN/LOSS into PENDING, so testing its result for "CASHOUT" never matched
  // and an entry settled early was shown as still pending.
  if (String(item.winStatus ?? "").trim().toUpperCase() === "CASHOUT") {
    return { variant: "won", ...settledEarlyStatus() };
  }

  const ticketWs = normalizeLegStatus(item.winStatus);
  if (ticketWs === "WIN") {
    return { label: "Won", variant: "won", detail: "Settled — win" };
  }
  if (ticketWs === "LOSS") {
    return { label: "Lost", variant: "lost", detail: "Settled — loss" };
  }

  const betType = item.betType;

  if (betType === "Straight") {
    const legWs = normalizeLegStatus(item.winStatus);
    const phase = getGamePhase(item.gameTime);

    if (legWs === "WIN") {
      return { label: "Won", variant: "won", detail: "Leg won — ticket updating" };
    }
    if (legWs === "LOSS") {
      return { label: "Lost", variant: "lost", detail: "Leg lost — ticket updating" };
    }
    if (phase === "upcoming") {
      return { label: "Pending", variant: "pending", detail: "Game not started yet" };
    }
    if (phase === "live") {
      return { label: "Live", variant: "live", detail: "Game in progress" };
    }
    if (phase === "ended") {
      return {
        label: "Grading",
        variant: "grading",
        detail: "Game ended — result processing (updates every ~10 min)",
      };
    }
    return { label: "Open", variant: "pending", detail: "Awaiting game time" };
  }

  if (betType === "Parlay" || betType === "Teaser") {
    const legs = Array.isArray(item.bettingData) ? item.bettingData : [];
    const total = legs.length;
    let wins = 0;
    let losses = 0;
    let pending = 0;
    let hasUpcoming = false;
    let hasLive = false;
    let hasEnded = false;

    legs.forEach((leg) => {
      const ls = normalizeLegStatus(leg.winStatus);
      if (ls === "WIN") wins += 1;
      else if (ls === "LOSS") losses += 1;
      else pending += 1;

      const phase = getGamePhase(leg.gameTime);
      if (phase === "upcoming") hasUpcoming = true;
      else if (phase === "live") hasLive = true;
      else if (phase === "ended") hasEnded = true;
    });

    const legsSummary =
      total > 0 ? `${wins}W · ${losses}L · ${pending} pending` : undefined;

    if (losses > 0) {
      return {
        label: "At risk",
        variant: "lost",
        detail:
          pending > 0
            ? `${losses} leg(s) lost — ${pending} still open`
            : `${losses} leg(s) lost — ticket settling`,
        legsSummary,
      };
    }

    if (wins > 0 && pending > 0) {
      return {
        label: "Partial",
        variant: "partial",
        detail: `${wins} of ${total} leg(s) won — ${pending} remaining`,
        legsSummary,
      };
    }

    if (hasLive) {
      return {
        label: "Live",
        variant: "live",
        detail: "At least one game is live",
        legsSummary,
      };
    }

    if (hasUpcoming && !hasLive && !hasEnded) {
      return {
        label: "Pending",
        variant: "pending",
        detail: "Games not started yet",
        legsSummary,
      };
    }

    if (hasEnded && pending > 0) {
      return {
        label: "Grading",
        variant: "grading",
        detail: `${pending} leg(s) awaiting grade (~20 min cycle)`,
        legsSummary,
      };
    }

    return {
      label: "Open",
      variant: "pending",
      detail: "Ticket open",
      legsSummary,
    };
  }

  return { label: "Open", variant: "pending", detail: "Ticket open" };
}

/** Single leg row (parlay/teaser modal). */
export function getLegDisplayStatus(leg) {
  if (!leg) return { label: "Pending", variant: "pending", detail: "" };
  const ws = normalizeLegStatus(leg.winStatus);
  if (ws === "WIN") return { label: "Won", variant: "won", detail: "Leg won" };
  if (ws === "LOSS") return { label: "Lost", variant: "lost", detail: "Leg lost" };
  const phase = getGamePhase(leg.gameTime);
  if (phase === "upcoming") return { label: "Pending", variant: "pending", detail: "Not started" };
  if (phase === "live") return { label: "Live", variant: "live", detail: "In progress" };
  if (phase === "ended") {
    return { label: "Grading", variant: "grading", detail: "Awaiting grade" };
  }
  return { label: "Pending", variant: "pending", detail: "" };
}
